import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createElementBlock as _createElementBlock, renderSlot as _renderSlot, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = ["data-testid"]
const _hoisted_2 = {
  key: 0,
  class: "modal__icon"
}
const _hoisted_3 = { class: "modal__title" }
const _hoisted_4 = {
  key: 1,
  class: "modal__description"
}
const _hoisted_5 = {
  key: 2,
  class: "modal__progress"
}
const _hoisted_6 = {
  key: 0,
  class: "modal__progress-label"
}
const _hoisted_7 = ["max", "value"]
const _hoisted_8 = { class: "modal__progress-meta" }
const _hoisted_9 = { class: "modal__progress-message" }
const _hoisted_10 = { class: "modal__progress-value" }
const _hoisted_11 = {
  key: 4,
  class: "modal__footer"
}
const _hoisted_12 = { class: "modal__button-container" }

import SvgLoader from '@/utils/SvgLoader.vue';
import Modal from '@/components/modal/Modal.vue';
import Button from '@/components/input/Btn/Btn.vue';
import { LottieAnimation } from 'lottie-web-vue';
import loadingAnimation from '@/assets/loadingAnimation.json';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';

/**
 * Iconの種類
 */
type IconType = '' | 'info' | 'error' | 'loading';
/**
 * colorStyleの種類
 */
type ColorStyle = 'primary' | 'error';


export default /*@__PURE__*/_defineComponent({
  __name: 'InfoModal',
  props: {
    modalState: { type: Boolean },
    backButtonLabel: { default: '' },
    nextButtonLabel: { default: '' },
    icon: { default: '' },
    colorStyle: { default: 'primary' },
    enableOutsideClick: { type: Boolean, default: true },
    modalWidth: { default: undefined },
    isDisabledNextButton: { type: Boolean, default: undefined },
    isDisabledScroll: { type: Boolean, default: undefined },
    noDescription: { type: Boolean, default: false },
    testId: { default: undefined },
    progressValue: { default: undefined },
    progressMax: { default: 100 },
    isCenterContent: { type: Boolean, default: false },
    useKeyboard: { type: Boolean, default: false }
  },
  emits: ['onBack', 'onNext'],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emits = __emit;

const iconPath = computed(() => {
  switch (props.icon) {
    case 'info':
      return 'modalInfoIcon';
    case 'error':
      return 'modalErrorInfoIcon';
    default:
      return '';
  }
});

const suffixMainColorCSS = computed(() => {
  switch (props.colorStyle) {
    case 'primary':
      return '--primary';
    case 'error':
      return '--error';
    default:
      return '--primary';
  }
});

const modalSize = computed(() => {
  return props.modalWidth ?? 'max-content';
});

const hasProgress = computed(() => props.progressValue !== undefined);

const progressPercentText = computed(() => {
  if (props.progressValue === undefined) return '';
  const max = props.progressMax ?? 100;
  const safeValue = Math.min(Math.max(props.progressValue, 0), max);
  return `${Math.round((safeValue / max) * 100)}%`;
});

/**
 * 戻るボタンクリック時の処理
 */
const handleBack = () => {
  if (props.modalState) {
    emits('onBack');
  }
};

/**
 * 次へボタンクリック時の処理
 */
const handleNext = () => {
  emits('onNext');
};

const nextButtonRef = ref<{ focus: () => void } | null>(null);

/**
 * ESCキー押下のイベントハンドラ
 * @param e - キーボードイベント
 */
const keydownEvent = (e: KeyboardEvent) => {
  if (e.key === 'Escape' && props.useKeyboard) {
    e.preventDefault();
    handleBack();
  }
};

/**
 * モーダルオープン時の処理
 */
const openModal = async () => {
  window.addEventListener('keydown', keydownEvent);
  if (props.useKeyboard) {
    await nextTick();
    nextButtonRef.value?.focus();
  }
};

/**
 * モーダルクローズ時の処理
 */
const closeModal = () => {
  window.removeEventListener('keydown', keydownEvent);
};

watch(
  () => props.modalState,
  async (nv) => {
    if (nv) {
      await openModal();
    } else {
      closeModal();
    }
  }
);

onMounted(async () => {
  if (props.modalState) await openModal();
});

onBeforeUnmount(() => {
  if (props.modalState) closeModal();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(Modal, {
    "modal-state": _ctx.modalState,
    padding: "0",
    "border-radius": "16px",
    "no-padding-and-margin": false,
    "modal-size": modalSize.value,
    "is-force-no-style": true,
    "md-click-out-side-to-close": _ctx.enableOutsideClick,
    "scroll-off": _ctx.isDisabledScroll,
    "no-cancel": true,
    onOutsideClick: handleBack
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", {
        class: "modal",
        "data-testid": _ctx.testId || undefined
      }, [
        (_ctx.icon !== '')
          ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
              (_ctx.icon === 'loading')
                ? (_openBlock(), _createBlock(_unref(LottieAnimation), {
                    key: 0,
                    ref: "anim",
                    "animation-data": _unref(loadingAnimation),
                    "auto-play": true,
                    loop: true,
                    speed: 1
                  }, null, 8, ["animation-data"]))
                : (_openBlock(), _createBlock(SvgLoader, {
                    key: 1,
                    class: _normalizeClass(`modal__icon--${iconPath.value}`),
                    name: iconPath.value
                  }, null, 8, ["class", "name"]))
            ]))
          : _createCommentVNode("", true),
        _createElementVNode("h1", _hoisted_3, [
          _renderSlot(_ctx.$slots, "title")
        ]),
        (!_ctx.noDescription)
          ? (_openBlock(), _createElementBlock("p", _hoisted_4, [
              _renderSlot(_ctx.$slots, "description")
            ]))
          : _createCommentVNode("", true),
        (hasProgress.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
              (_ctx.$slots.progressLabel)
                ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                    _renderSlot(_ctx.$slots, "progressLabel")
                  ]))
                : _createCommentVNode("", true),
              _createElementVNode("progress", {
                class: "modal__progress-bar",
                max: _ctx.progressMax,
                value: _ctx.progressValue
              }, null, 8, _hoisted_7),
              _createElementVNode("div", _hoisted_8, [
                _createElementVNode("div", _hoisted_9, [
                  _renderSlot(_ctx.$slots, "progressMessage")
                ]),
                _createElementVNode("span", _hoisted_10, _toDisplayString(progressPercentText.value), 1)
              ])
            ]))
          : _createCommentVNode("", true),
        (_ctx.$slots.default)
          ? (_openBlock(), _createElementBlock("div", {
              key: 3,
              class: _normalizeClass(["modal__body", { 'modal__body--center': _ctx.isCenterContent }])
            }, [
              _renderSlot(_ctx.$slots, "default")
            ], 2))
          : _createCommentVNode("", true),
        (_ctx.backButtonLabel !== '' || _ctx.nextButtonLabel !== '')
          ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
              _createElementVNode("div", _hoisted_12, [
                (_ctx.backButtonLabel !== '')
                  ? (_openBlock(), _createBlock(Button, {
                      key: 0,
                      class: _normalizeClass(`modal__button modal__button--cancel modal__button--cancel${suffixMainColorCSS.value}`),
                      onClick: handleBack
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_ctx.backButtonLabel), 1)
                      ], undefined, true),
                      _: 1
                    }, 8, ["class"]))
                  : _createCommentVNode("", true),
                _renderSlot(_ctx.$slots, "betweenButtons"),
                (_ctx.nextButtonLabel !== '')
                  ? (_openBlock(), _createBlock(Button, {
                      key: 1,
                      ref_key: "nextButtonRef",
                      ref: nextButtonRef,
                      class: _normalizeClass(`modal__button modal__button--save modal__button--save${suffixMainColorCSS.value}`),
                      disabled: _ctx.isDisabledNextButton,
                      onClick: handleNext
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_ctx.nextButtonLabel), 1)
                      ], undefined, true),
                      _: 1
                    }, 8, ["class", "disabled"]))
                  : _createCommentVNode("", true)
              ]),
              _renderSlot(_ctx.$slots, "footer")
            ]))
          : _createCommentVNode("", true)
      ], 8, _hoisted_1)
    ], undefined, true),
    _: 3
  }, 8, ["modal-state", "modal-size", "md-click-out-side-to-close", "scroll-off"]))
}
}

})